/**
 * Which node parts render without boundary comments (#1354, #1390).
 *
 * A node part is normally bracketed by `<!--h:N-->…<!--/h:N-->` so it knows
 * where its content begins and ends among siblings. Two shapes do not need
 * them, and both are decided once per compiled template:
 *
 * - a marker that is its parent element's ONLY content (#1354): the parent's
 *   own boundaries already say exactly that. The comparator's row carried
 *   four such comments, which is why Hydrogen rendered 13 DOM nodes where
 *   React renders 9.
 * - a marker bounded by static sibling nodes (#1390): when the string proves
 *   an element (or void element) immediately before it — across at most one
 *   authoring-whitespace run — and an opening tag after it, the adjacent
 *   sibling nodes themselves bound the range and the comments say nothing.
 *   A `tbody`/`thead`/`tfoot` hole whose `<table>` ancestor is in the same
 *   static HTML is the board's case of the first shape: the parser relocates
 *   a table section only outside a table, and the ancestor proves the
 *   context.
 *
 * The decision is derived ONCE per compiled `Template` and memoized on it, so
 * the three paths that must agree — `renderToString`/`renderToChunks`, the
 * mount path, and hydration — cannot disagree. It is a static predicate over
 * `staticHtml` rather than over a parsed tree because server rendering runs in
 * a Cloudflare Worker with no DOM, and a DOM-derived answer could not be
 * reached there at all.
 *
 * **It fails closed.** Every check below is a reason to KEEP the markers, and
 * keeping them is always correct — eliding them wrongly is not. An attribute
 * value containing `>`, a parent the HTML parser relocates or retokenizes its
 * children for, a self-closing or void parent, a marker appearing more than
 * once, a neighbour that is text or another comment, a close tag whose
 * element the parser may not actually pop: all of them keep their markers
 * rather than being reasoned about. Where the string's reading and the
 * parsed tree could disagree only for MALFORMED static HTML, the mount path
 * verifies the promised neighbours structurally and throws rather than
 * binding a range the server's bytes cannot describe.
 */
import { isAttributePartType, isElementPartType } from './render-values.js';
/**
 * Parents a node part may not shed its markers inside.
 *
 * Three families, all of them cases where the element's textual children in
 * `staticHtml` are not its DOM children:
 *
 * - **void** elements have no children at all, so `<br><!--h:0--></br>` puts
 *   the comment BESIDE the `<br>`, not inside it;
 * - **raw-text** elements tokenize their content as text, so the "comment" is
 *   never a comment node;
 * - **restructuring** parents are where the parser foster-parents, implies
 *   tags, or drops content: everything in the table and select families, plus
 *   the document-level elements and `<template>`, whose children land in a
 *   separate `content` tree.
 *
 * `tbody`/`thead`/`tfoot` are re-admitted by `soleContentParent` when the
 * `<table>` ancestor is provable in the same static HTML (#1390).
 */
const UNSAFE_RANGE_PARENTS = new Set(('area base br col embed hr img input link meta source track wbr ' +
    'script style textarea title xmp iframe noembed noframes noscript plaintext ' +
    'table thead tbody tfoot tr select optgroup colgroup template html head body frameset').split(' '));
/**
 * A complete, unambiguous opening tag.
 *
 * `[^>]*` cannot cross a `>`, so an attribute value carrying one
 * (`<div title="a>b">`) simply does not match and keeps its markers — the
 * quote-aware parse that would resolve it is exactly the certainty this
 * predicate declines to claim. A `/` before the `>` is not `\s`-led attribute
 * text either, so `<br/>` and `<circle/>` fail here too.
 */
const OPEN_TAG_RE = /^<([a-z][a-z0-9-]*)(?:\s[^>]*)?>$/i;
/** Shared for every template that elides nothing — the overwhelming majority. */
const NO_ELISION = new Set();
/**
 * HTML whitespace: the only characters a text node may be made of to count as
 * an authoring-formatting run rather than content (#1312).
 */
function isHtmlWhitespace(code) {
    return code === 32 || code === 9 || code === 10 || code === 12 || code === 13;
}
/**
 * The lowercased tag name of the element whose only content is `marker`, or
 * null when that is not provably the case.
 *
 * `table`-section parents (`tbody`, `thead`, `tfoot`) are the one exception
 * to `UNSAFE_RANGE_PARENTS` (#1390): the parser relocates them only when no
 * `<table>` is open, and a `<table>` opened in the same static HTML and not
 * closed before the marker proves the context. Outside that proof they keep
 * their markers like the rest of the family.
 */
function soleContentParent(staticHtml, marker) {
    const opening = `<!--${marker}-->`;
    const at = staticHtml.indexOf(opening);
    // Position 0 has no parent tag before it; a second occurrence means the
    // renderers' first-match replacement is not provably this one.
    if (at <= 0 || staticHtml.indexOf(opening, at + 1) >= 0)
        return null;
    // Anything but an opening tag immediately before — text, whitespace, a
    // sibling element, another comment — means the part is not the sole content.
    if (staticHtml.charCodeAt(at - 1) !== 62 /* > */)
        return null;
    const open = staticHtml.lastIndexOf('<', at - 1);
    if (open < 0)
        return null;
    const tag = OPEN_TAG_RE.exec(staticHtml.slice(open, at));
    const name = tag?.[1]?.toLowerCase();
    if (!name)
        return null;
    if (name === 'tbody' || name === 'thead' || name === 'tfoot') {
        if (!tableOpenBefore(staticHtml, at))
            return null;
    }
    else if (UNSAFE_RANGE_PARENTS.has(name)) {
        return null;
    }
    // The parent must close immediately after the marker, on the same element.
    const close = `</${name}>`;
    const after = at + opening.length;
    if (staticHtml.slice(after, after + close.length).toLowerCase() !== close)
        return null;
    return name;
}
/**
 * Whether a `<table>` opened in `staticHtml` before `at` and has not closed.
 *
 * A backward scan over `<table`/`</table` tag tokens only: the table family
 * cannot interleave with anything else at this depth, so for well-formed
 * static HTML the count is exact, and for malformed HTML the mount path's
 * structural check (#1390) refuses the bind rather than trusting the count.
 */
function tableOpenBefore(staticHtml, at) {
    let depth = 0;
    let cursor = at;
    for (;;) {
        const open = tableTagBackward(staticHtml, cursor, '<table');
        const close = tableTagBackward(staticHtml, cursor, '</table');
        if (close >= 0 && close > open) {
            depth += 1;
            cursor = close;
            continue;
        }
        if (open < 0)
            return false;
        if (depth === 0)
            return true;
        depth -= 1;
        cursor = open;
    }
}
/**
 * The last `<table`/`</table` token starting strictly before `limit`, or -1.
 * A prefix match is only a tag when the name ends there — `<tableX>` and
 * `<tableau>` are other elements entirely.
 */
function tableTagBackward(staticHtml, limit, prefix) {
    let at = staticHtml.lastIndexOf(prefix, limit - 1);
    while (at >= 0) {
        const code = staticHtml.charCodeAt(at + prefix.length);
        if (code === 62 /* > */ || isHtmlWhitespace(code))
            return at;
        at = staticHtml.lastIndexOf(prefix, at - 1);
    }
    return -1;
}
/**
 * End tags that must not be read as "the element before the hole": the
 * active-formatting set, whose misnested closes reconstruct wrappers between
 * themselves and whatever follows, plus `br`/`p`, whose end tags the parser
 * reinterprets (inserting a `<br>`, implying a `<p>`) rather than closing.
 */
const UNSAFE_CLOSE_SIBLINGS = {
    a: true,
    b: true,
    big: true,
    code: true,
    em: true,
    font: true,
    i: true,
    nobr: true,
    s: true,
    small: true,
    strike: true,
    strong: true,
    tt: true,
    u: true,
    br: true,
    p: true,
};
/** Void elements: an open tag is their whole story, so a marker after one is its sibling. */
const VOID_SIBLINGS = {
    area: true,
    base: true,
    br: true,
    col: true,
    embed: true,
    hr: true,
    img: true,
    input: true,
    link: true,
    meta: true,
    source: true,
    track: true,
    wbr: true,
};
/**
 * Start tags an end bound may not be built from when they touch the marker:
 * every tag that implies the close of an open element (the `<p>`-closers, the
 * list and table families, `option`/`optgroup`), the active-formatting set,
 * which reconstruction can relocate, and the raw-text/secondary-tree
 * elements, whose children the parser does not treat as markup.
 */
const UNSAFE_OPEN_SIBLINGS = {
    address: true,
    article: true,
    aside: true,
    blockquote: true,
    center: true,
    details: true,
    dialog: true,
    dir: true,
    div: true,
    dl: true,
    dd: true,
    dt: true,
    fieldset: true,
    figcaption: true,
    figure: true,
    footer: true,
    form: true,
    h1: true,
    h2: true,
    h3: true,
    h4: true,
    h5: true,
    h6: true,
    header: true,
    hgroup: true,
    hr: true,
    li: true,
    main: true,
    menu: true,
    nav: true,
    ol: true,
    p: true,
    plaintext: true,
    pre: true,
    section: true,
    summary: true,
    table: true,
    ul: true,
    xmp: true,
    listing: true,
    caption: true,
    col: true,
    colgroup: true,
    tbody: true,
    td: true,
    tfoot: true,
    th: true,
    thead: true,
    tr: true,
    option: true,
    optgroup: true,
    a: true,
    b: true,
    big: true,
    code: true,
    em: true,
    font: true,
    i: true,
    nobr: true,
    s: true,
    small: true,
    strike: true,
    strong: true,
    tt: true,
    u: true,
    script: true,
    style: true,
    textarea: true,
    title: true,
    iframe: true,
    noembed: true,
    noframes: true,
    noscript: true,
    template: true,
};
/**
 * Whether the `</name>` (or void-element open) ending at `before` proves an
 * element sibling immediately before — or, across one whitespace run, behind
 * — the marker starting at `at`.
 *
 * A literal close tag pops the stack down through its element, so what
 * follows it is that element's sibling — except for the active-formatting
 * elements, whose adoption-agency reconstruction can leave a fresh empty
 * wrapper in between, and except for the tags whose "close" the parser
 * reinterprets or ignores outright. A void open tag never has content, so a
 * marker after one is always its sibling. Any disagreement between this
 * string-level reading and the tree the parser actually builds is caught by
 * the mount path's structural check, which throws rather than half-binding.
 */
function boundBefore(staticHtml, at) {
    let edge = at - 1;
    while (edge >= 0 && isHtmlWhitespace(staticHtml.charCodeAt(edge)))
        edge -= 1;
    const whitespace = edge < at - 1;
    if (edge < 0 || staticHtml.charCodeAt(edge) !== 62 /* > */)
        return false;
    const open = staticHtml.lastIndexOf('<', edge);
    if (open < 0)
        return false;
    const tag = staticHtml.slice(open, edge + 1);
    const close = /^<\/([a-z][a-z0-9-]*)\s*>$/i.exec(tag);
    if (close) {
        // Only a bound that IS the element cares which element it is. Across a
        // whitespace run the bound is the text node — already inserted wherever
        // the close left the insertion point, however the parser treated it — so
        // even a close the parser may have mishandled proves a bound.
        if (!whitespace && UNSAFE_CLOSE_SIBLINGS[close[1]?.toLowerCase() ?? ''])
            return false;
        return true;
    }
    const openTag = OPEN_TAG_RE.exec(tag);
    return !!(openTag && VOID_SIBLINGS[openTag[1]?.toLowerCase() ?? '']);
}
/**
 * Whether an opening tag starting at `after` proves an element sibling
 * immediately after — or, across one whitespace run, ahead of — the marker
 * that ends at `before`.
 *
 * When the tags touch, the element itself becomes the part's end bound, so
 * its start tag must be one that cannot imply the close of an open element
 * (a `<div>` after a hole in a `<p>` becomes the `<p>`'s sibling) and cannot
 * be adoption-agency reconstructed: those land in the wrong parent and the
 * part would insert across parents. Across whitespace the bound is the text
 * node — inserted before the element is even seen, immune to everything the
 * element may do — so any unambiguous opening tag proves it.
 */
function boundAfter(staticHtml, before) {
    const len = staticHtml.length;
    let edge = before;
    while (edge < len && isHtmlWhitespace(staticHtml.charCodeAt(edge)))
        edge += 1;
    const whitespace = edge > before;
    if (edge >= len || staticHtml.charCodeAt(edge) !== 60 /* < */)
        return false;
    const close = staticHtml.indexOf('>', edge);
    if (close < 0)
        return false;
    const tag = OPEN_TAG_RE.exec(staticHtml.slice(edge, close + 1));
    const name = tag?.[1]?.toLowerCase();
    if (!name)
        return false;
    if (!whitespace && UNSAFE_OPEN_SIBLINGS[name])
        return false;
    return true;
}
/**
 * Whether `marker`'s neighbours prove a sibling-bounded range: an element or
 * void-open tag (possibly across one whitespace run) before it, an opening
 * tag after it, and — when the tags touch — the after-tag safe to hang a
 * bound from. The marker must also be unique, on the same terms the
 * first-match renderers replace by.
 */
function siblingBounded(staticHtml, marker) {
    const opening = `<!--${marker}-->`;
    const at = staticHtml.indexOf(opening);
    if (at < 0 || staticHtml.indexOf(opening, at + 1) >= 0)
        return false;
    return boundBefore(staticHtml, at) && boundAfter(staticHtml, at + opening.length);
}
/**
 * Foreign content (G12) needs no exception here, and must not have one: an
 * `svg` template and an `html` template with the same markup are required to
 * string-render byte for byte, so a namespace-shaped carve-out would be a
 * divergence rather than a safety margin. Nothing this predicate tests behaves
 * differently in foreign content — there is no foster parenting there, the
 * self-closing syntax it admits (`<circle/>`) is already rejected by
 * `OPEN_TAG_RE`, the raw-text and integration-point elements that do exist
 * (`script`, `style`, `title`) are already blocked, and the case-sensitive tag
 * names compare lowercased on both sides.
 */
function computeElision(template) {
    let elided = null;
    let sibling = null;
    for (const spec of template.partSpecs) {
        if (isAttributePartType(spec.type) || isElementPartType(spec.type))
            continue;
        if (soleContentParent(template.staticHtml, spec.marker)) {
            (elided ??= new Set()).add(spec.marker);
            continue;
        }
        if (siblingBounded(template.staticHtml, spec.marker)) {
            (elided ??= new Set()).add(spec.marker);
            (sibling ??= new Set()).add(spec.marker);
        }
    }
    return { elided: elided ?? NO_ELISION, sibling: sibling ?? NO_ELISION };
}
/**
 * One derivation answering both questions, stamped on the template's two
 * memos. Every reader goes through here, so whichever question is asked
 * first, both answers exist — and stay identical for the template's life.
 */
function elisionOf(template) {
    const elided = template.elidedMarkers;
    const sibling = template.siblingElidedMarkers;
    if (elided && sibling)
        return { elided, sibling };
    const computed = computeElision(template);
    template.elidedMarkers = computed.elided;
    template.siblingElidedMarkers = computed.sibling;
    return computed;
}
/**
 * The node-part markers this template renders without boundary comments.
 *
 * Memoized on the `Template` itself: one derivation per compiled template,
 * shared by string rendering, the mount path and hydration, so the three can
 * never reach different answers for the same hole.
 */
export function elidedNodeMarkers(template) {
    return elisionOf(template).elided;
}
/**
 * The elided node-part markers whose holes are bounded by static siblings
 * rather than by their parent element (#1390). A subset of
 * `elidedNodeMarkers`; see `Template.siblingElidedMarkers`.
 */
export function siblingBoundedMarkers(template) {
    return elisionOf(template).sibling;
}
//# sourceMappingURL=render-part-elision.js.map